import _defineProperty from "/home/circleci/repo/node_modules/babel-preset-react-app/node_modules/@babel/runtime/helpers/esm/defineProperty";
import _objectWithoutProperties from "/home/circleci/repo/node_modules/babel-preset-react-app/node_modules/@babel/runtime/helpers/esm/objectWithoutProperties";

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys(source, true).forEach(function (key) { _defineProperty(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys(source).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }

import React, { useCallback, useEffect, useRef } from 'react';
import { useMergedRef } from '../../hooks/useMergedRef';
import { useSpacing } from '../../api/spacing/useSpacing';
import { useIndicator } from './useIndicator';
import { useScroll } from './useScroll';
import { StyledList, StyledItem, StyledBlockIndicator, StyledLineIndicator, StyledScrollContainer, StyledScrollControl, StyledScrollIcon, StyledContainer } from './GenericNavigation.styles'; // This number represents the percentage of the scrollable area to scroll by.

var scrollMultiplier = 0.75;

var noop = function noop() {};

export var GenericNavigationList = React.forwardRef(function (_ref, ref) {
  var testId = _ref['data-testid'],
      children = _ref.children,
      className = _ref.className,
      id = _ref.id,
      _ref$size = _ref.size,
      size = _ref$size === void 0 ? 'large' : _ref$size,
      _ref$variant = _ref.variant,
      variant = _ref$variant === void 0 ? 'text' : _ref$variant,
      _ref$scrollButtons = _ref.scrollButtons,
      scrollButtons = _ref$scrollButtons === void 0 ? 'auto' : _ref$scrollButtons,
      _ref$selectionFollows = _ref.selectionFollowsFocus,
      selectionFollowsFocus = _ref$selectionFollows === void 0 ? false : _ref$selectionFollows,
      _ref$itemType = _ref.itemType,
      itemType = _ref$itemType === void 0 ? GenericNavigationItem : _ref$itemType,
      selected = _ref.selected,
      _ref$onSelect = _ref.onSelect,
      onSelect = _ref$onSelect === void 0 ? noop : _ref$onSelect,
      restProps = _objectWithoutProperties(_ref, ["data-testid", "children", "className", "id", "size", "variant", "scrollButtons", "selectionFollowsFocus", "itemType", "selected", "onSelect"]);

  var isItem = useCallback(function (element) {
    return React.isValidElement(element) && element.type === itemType;
  }, [itemType]);
  var listRef = useRef(null);
  var mergedListRef = useMergedRef(listRef, ref);
  var selectedItemRef = useRef(null);
  var handleSelect = useCallback(function (nextSelected, event) {
    var _a, _b;

    var disabled = (_b = (_a = listRef.current) === null || _a === void 0 ? void 0 : _a.querySelector("#".concat(nextSelected, " a"))) === null || _b === void 0 ? void 0 : _b.hasAttribute('aria-disabled');

    if (!disabled) {
      onSelect(nextSelected, event);
    }
  }, [listRef, onSelect]);

  var makeClickHandler = function makeClickHandler(nextSelected) {
    return function (event) {
      if (nextSelected !== selected) handleSelect(nextSelected, event);
    };
  };

  var isRtl = (listRef === null || listRef === void 0 ? void 0 : listRef.current) && getComputedStyle(listRef === null || listRef === void 0 ? void 0 : listRef.current).direction === 'rtl';

  var keyDownHandler = function keyDownHandler(event) {
    var _a, _b;

    if (event.key === 'Tab') return;
    event.preventDefault();
    var navItems = Array.from((_b = (_a = listRef.current) === null || _a === void 0 ? void 0 : _a.querySelectorAll("".concat(StyledItem, " a"))) !== null && _b !== void 0 ? _b : []);
    var focusIndex = navItems.findIndex(function (element) {
      return element === event.target;
    });

    if ([' ', 'Enter'].includes(event.key)) {
      handleSelect(navItems[focusIndex].parentElement.getAttribute('id'), event);
      return;
    }

    var prevKey = isRtl ? 'ArrowRight' : 'ArrowLeft';
    var nextKey = isRtl ? 'ArrowLeft' : 'ArrowRight';
    var nextFocusIndex = focusIndex;

    switch (event.key) {
      case 'Home':
        nextFocusIndex = 0;
        break;

      case 'End':
        nextFocusIndex = navItems.length - 1;
        break;

      case prevKey:
        nextFocusIndex = focusIndex === 0 ? navItems.length - 1 : focusIndex - 1;
        break;

      case nextKey:
        nextFocusIndex = (focusIndex + 1) % navItems.length;
        break;
    }

    if (nextFocusIndex !== focusIndex) {
      var nextSelected = navItems[nextFocusIndex];
      if (nextSelected) nextSelected.focus();

      if (selectionFollowsFocus) {
        handleSelect(navItems[nextFocusIndex].parentElement.getAttribute('id'), event);
      }
    }
  };

  var indicatorReady = useIndicator(listRef, selectedItemRef, [selected, size]);
  var scrollContainerRef = useRef(null);
  var scrollContentsRef = useRef(null);

  var _useScroll = useScroll(scrollContainerRef, scrollContentsRef, [size]),
      canScroll = _useScroll.canScroll,
      canScrollBackward = _useScroll.canScrollBackward,
      canScrollForward = _useScroll.canScrollForward,
      scrollIntoView = _useScroll.scrollIntoView,
      scrollBy = _useScroll.scrollBy;

  var showScrollButtons = scrollButtons === 'always' || scrollButtons === 'auto' && canScroll;
  var scrollByViewWidth = useCallback(function (multiplier) {
    return function () {
      return scrollContainerRef.current && scrollBy({
        left: scrollContainerRef.current.offsetWidth * multiplier,
        behavior: 'smooth'
      });
    };
  }, [scrollContainerRef, scrollBy]);
  var disableScrollPrevious = !canScrollBackward;
  var disableScrollNext = !canScrollForward;
  var scrollPrevious = scrollByViewWidth(isRtl ? scrollMultiplier : -scrollMultiplier);
  var scrollNext = scrollByViewWidth(isRtl ? -scrollMultiplier : scrollMultiplier);
  useEffect(function () {
    scrollIntoView(selectedItemRef);
  }, [scrollIntoView, selected]);
  return React.createElement(StyledContainer, {
    className: className,
    "data-testid": testId,
    id: id,
    outerSpacing: useSpacing(restProps),
    ref: mergedListRef,
    size: size
  }, showScrollButtons && React.createElement(StyledScrollControl, _objectSpread({
    position: "start"
  }, disableScrollPrevious && {
    disabled: true
  }, {
    onClick: scrollPrevious
  }), React.createElement(ScrollIcon, null)), React.createElement(StyledScrollContainer, {
    as: variant === 'text' ? 'nav' : 'div',
    ref: scrollContainerRef
  }, indicatorReady && variant === 'tabs' && React.createElement(StyledBlockIndicator, null), React.createElement(StyledList, {
    role: variant === 'tabs' ? 'tablist' : undefined,
    ref: scrollContentsRef
  }, React.Children.map(children, function (child) {
    if (isItem(child)) {
      var isSelected = child.props.id === selected;
      var modified = React.cloneElement(child, _objectSpread({
        selected: isSelected,
        onClick: makeClickHandler(child.props.id),
        onKeyDown: keyDownHandler
      }, isSelected && {
        ref: selectedItemRef
      }, {
        variant: variant
      }));
      return modified;
    } else {
      return child;
    }
  })), indicatorReady && React.createElement(StyledLineIndicator, null)), showScrollButtons && React.createElement(StyledScrollControl, _objectSpread({
    position: "end"
  }, disableScrollNext && {
    disabled: true
  }, {
    onClick: scrollNext
  }), React.createElement(ScrollIcon, null)));
});
GenericNavigationList.displayName = 'GenericNavigationList';
export var GenericNavigationItem = React.forwardRef(function (_ref2, ref) {
  var children = _ref2.children,
      id = _ref2.id,
      selected = _ref2.selected,
      _ref2$onClick = _ref2.onClick,
      onClick = _ref2$onClick === void 0 ? noop : _ref2$onClick,
      _ref2$onKeyDown = _ref2.onKeyDown,
      onKeyDown = _ref2$onKeyDown === void 0 ? noop : _ref2$onKeyDown,
      _ref2$variant = _ref2.variant,
      variant = _ref2$variant === void 0 ? 'text' : _ref2$variant,
      controlRef = _ref2.controlRef,
      controlId = _ref2.controlId,
      ariaControls = _ref2['aria-controls'],
      disabled = _ref2.disabled,
      href = _ref2.href;
  var handleClick = useCallback(function (event) {
    if (!href) event.preventDefault();
    onClick(event);
  }, [href, onClick]);

  var props = _objectSpread({
    onClick: handleClick,
    ref: controlRef,
    id: controlId,
    href: href !== null && href !== void 0 ? href : '#'
  }, disabled && {
    /* Do not use 'disabled' HTML attribute; keep item focusable per WAI guidelines */
    'aria-disabled': true
  }, {}, variant === 'tabs' ? {
    role: 'tab',
    'aria-controls': ariaControls,
    'aria-selected': selected,
    onKeyDown: onKeyDown,

    /* Note: tabIndex here refers to navigation by the "tab" key on the keyboard.
     * Only the selected tab should be in the page's tab sequence */
    tabIndex: selected ? 0 : -1
  } : _objectSpread({}, !href && {
    role: 'button'
  }, {}, selected && {
    'aria-current': true
  }));

  return React.createElement(StyledItem, _objectSpread({}, variant === 'tabs' && {
    role: 'presentation'
  }, {
    variant: variant,
    id: id,
    ref: ref
  }), React.createElement("a", _objectSpread({}, props), children));
});
GenericNavigationItem.displayName = 'GenericNavigationItem';

var ScrollIcon = function ScrollIcon() {
  return React.createElement(StyledScrollIcon, {
    width: "24",
    height: "24",
    viewBox: "0 0 24 24",
    xmlns: "http://www.w3.org/2000/svg"
  }, React.createElement("path", {
    d: "M10 7L15 12 10 17",
    stroke: "currentColor",
    strokeWidth: "1",
    fill: "none",
    fillRule: "evenodd"
  }));
};